import React, { useEffect, useRef, useState } from 'react';
import { Namespaces } from './Namespaces';

interface DeferredSectionProps {
  children: React.ReactNode;
  anchorId?: string;
  reserveClassName: string;
  rootMargin?: string;
}

/**
 * Keeps below-the-fold React trees out of the initial render.
 * The reserved space lets anchor navigation and scrolling remain predictable
 * while the next section is fetched before it enters the viewport.
 */
export function DeferredSection({ children, anchorId, reserveClassName, rootMargin = '1600px 0px' }: DeferredSectionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(() => (
    typeof window !== 'undefined' && anchorId !== undefined && window.location.hash === `#${anchorId}`
  ));

  useEffect(() => {
    if (isReady || !rootRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsReady(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [isReady]);

  return (
    <div
      ref={rootRef}
      id={isReady ? undefined : anchorId}
      className={isReady ? undefined : reserveClassName}
      aria-busy={!isReady}
    >
      {isReady ? children : null}
    </div>
  );
}

/**
 * Same job as `Namespaces`, for the below-the-fold home sections: their chunk and
 * their dictionary namespace start loading at the same moment the section comes
 * into view, and the section only mounts once the strings are there. Renders
 * nothing while waiting, which is invisible here because the reserved space is
 * still on screen.
 */
export function DeferredSectionWithNamespaces({
  namespaces,
  children,
  ...rest
}: Omit<DeferredSectionProps, 'children'> & { namespaces: readonly string[]; children: React.ReactNode }) {
  return (
    <DeferredSection {...rest}>
      <Namespaces namespaces={namespaces} fallback={<div className={rest.reserveClassName} aria-busy="true" />}>
        {children}
      </Namespaces>
    </DeferredSection>
  );
}
